/**
 * Fixture data for the dev-only persona switcher (DevPersonaSwitcher.jsx).
 *
 * Never imported by anything that ships: lib/supabase.js only reaches into
 * this file inside an `import.meta.env.DEV` branch, which Vite replaces with
 * a static `false` in a production build (`npm run build`), and the branch
 * that imports this module is dead code from that point on.
 *
 * One shared "world" — one church, one set of songs and people — built once
 * and reused across every persona, so switching between Pat the admin and Sam
 * the reviewer shows the same church rather than two disconnected demos.
 * Deliberately not exhaustive: covers each persona's landing screen and one
 * click deep, not the whole app. Extend by adding rows to the tables below.
 */

const CHURCH_ID = 'dev-church-1'

const CHURCH = {
  id: CHURCH_ID,
  name: 'Riverside Community Church',
  city: 'Melbourne',
  state: 'VIC',
  country: 'AU',
  contact_email: 'office@riverside.test',
  require_senior_signoff: true,
  show_writers_full_trail: true,
  allow_voice_memo_only: false,
  two_sunday_test: true,
  process_configured: true,
}

const USERS = {
  alex: { id: 'dev-alex', email: 'alex@dev.local', first_name: 'Alex', last_name: 'Rivers', role: 'songwriter' },
  jordan: { id: 'dev-jordan', email: 'jordan@dev.local', first_name: 'Jordan', last_name: 'Lee', role: 'songwriter' },
  pat: { id: 'dev-pat', email: 'pat@dev.local', first_name: 'Pat', last_name: 'Whitfield', role: 'songwriter' },
  sam: { id: 'dev-sam', email: 'sam@dev.local', first_name: 'Sam', last_name: 'Okafor', role: 'songwriter' },
  spm: { id: 'dev-spm', email: 'spm@dev.local', first_name: 'SPM', last_name: 'Staff', role: 'superadmin' },
}

const CHURCH_MEMBERS = [
  { id: 'cm-alex', user_id: USERS.alex.id, church_id: CHURCH_ID, role: 'songwriter',
    membership_status: 'approved', verified: true, staff_title: null,
    users: pick(USERS.alex, ['id', 'first_name', 'last_name', 'email']), churches: CHURCH },
  { id: 'cm-jordan', user_id: USERS.jordan.id, church_id: CHURCH_ID, role: 'songwriter',
    membership_status: 'approved', verified: true, staff_title: null,
    users: pick(USERS.jordan, ['id', 'first_name', 'last_name', 'email']), churches: CHURCH },
  { id: 'cm-pat', user_id: USERS.pat.id, church_id: CHURCH_ID, role: 'admin',
    membership_status: 'approved', verified: true, staff_title: 'Executive Pastor',
    users: pick(USERS.pat, ['id', 'first_name', 'last_name', 'email']), churches: CHURCH },
  { id: 'cm-sam', user_id: USERS.sam.id, church_id: CHURCH_ID, role: 'reviewer',
    membership_status: 'approved', verified: true, staff_title: 'Worship Pastor',
    can_leave_freetext: true, can_use_canned_responses: true,
    can_set_meetings: true, can_approve_members: true,
    users: pick(USERS.sam, ['id', 'first_name', 'last_name', 'email']), churches: CHURCH },
  // A pending songwriter, so People.jsx has something in "Waiting on you".
  { id: 'cm-pending', user_id: 'dev-morgan', church_id: CHURCH_ID, role: 'songwriter',
    membership_status: 'pending', verified: false, staff_title: null,
    users: { id: 'dev-morgan', first_name: 'Morgan', last_name: 'Diaz', email: 'morgan@dev.local' },
    churches: CHURCH },
]

const SONG_COWRITERS = [
  { id: 'cw-heart-1', song_id: 'dev-song-heart', cowriter_name: 'Alex Rivers',
    split_percentage: 100, signed: true, signed_at: daysAgo(40), user_id: USERS.alex.id, is_primary: true },
  { id: 'cw-open-1', song_id: 'dev-song-open', cowriter_name: 'Alex Rivers',
    split_percentage: 50, signed: true, signed_at: daysAgo(3), user_id: USERS.alex.id, is_primary: true },
  { id: 'cw-open-2', song_id: 'dev-song-open', cowriter_name: 'Jordan Lee',
    split_percentage: 50, signed: false, signed_at: null, user_id: USERS.jordan.id, is_primary: false },
  { id: 'cw-trust-1', song_id: 'dev-song-trust', cowriter_name: 'Alex Rivers',
    split_percentage: 100, signed: true, signed_at: daysAgo(10), user_id: USERS.alex.id, is_primary: true },
]

const songCowriters = (songId) => SONG_COWRITERS.filter((c) => c.song_id === songId)

// created_at is not decoration: Drafts renders `new Date(s.created_at)`, and a
// fixture without one printed "Started Invalid Date" on every row. Any new
// fixture row that a screen dates needs this field.
const SONGS = [
  {
    id: 'dev-song-heart', songwriter_id: USERS.alex.id, title: 'Still My Heart',
    created_at: daysAgo(50),
    status: 'ready_to_send', destination: 'church', reach: 'church',
    key: 'G', scripture_refs: ['Psalm 46:10'],
    finished_at: daysAgo(35), submitted_at: null, submitted_to_church_id: null,
    r2_object_key: null, decl_original: true, decl_free_to_share: true,
    song_cowriters: songCowriters('dev-song-heart'),
  },
  {
    id: 'dev-song-open', songwriter_id: USERS.alex.id, title: 'Wide Open',
    created_at: daysAgo(21),
    status: 'awaiting_cosign', destination: null, reach: 'private',
    key: null, scripture_refs: [],
    finished_at: null, submitted_at: null, submitted_to_church_id: null,
    r2_object_key: null,
    song_cowriters: songCowriters('dev-song-open'),
  },
  {
    id: 'dev-song-trust', songwriter_id: USERS.alex.id, title: 'Learning To Trust',
    created_at: daysAgo(24),
    status: 'in_review', destination: 'church', reach: 'church',
    key: 'A', scripture_refs: ['Psalm 27:1'], season_note: 'Written after a hard year',
    finished_at: daysAgo(9), submitted_at: daysAgo(2), submitted_to_church_id: CHURCH_ID,
    // A recording, so the lyric-sync screen has something to tap along to in
    // persona mode. Maps to a real file in mockSupabaseClient.
    r2_object_key: 'dev/learning-to-trust.mp4', duration_seconds: 214,
    decl_original: true, decl_free_to_share: true,
    song_number: null,
    users: { first_name: 'Alex', last_name: 'Rivers' },
    song_cowriters: songCowriters('dev-song-trust'),
  },
  {
    id: 'dev-song-draft', songwriter_id: USERS.alex.id, title: 'Untitled',
    created_at: daysAgo(3),
    status: 'draft', destination: null, reach: 'private',
    key: null, scripture_refs: [],
    finished_at: null, submitted_at: null, submitted_to_church_id: null, r2_object_key: null,
    song_cowriters: [],
  },
]

const SONG_SECTIONS = [
  { id: 'sec-trust-1', song_id: 'dev-song-trust', section_type: 'verse', section_number: 1,
    order_position: 1, lyrics: 'In the quiet I have questioned\nWhether You could hear my voice' },
  { id: 'sec-trust-2', song_id: 'dev-song-trust', section_type: 'chorus', section_number: 1,
    order_position: 2, lyrics: "Still I'm learning to trust You\nEven when I cannot see" },
  { id: 'sec-open-1', song_id: 'dev-song-open', section_type: 'verse', section_number: 1,
    order_position: 1, lyrics: 'Wide open, arms open\nNothing held back from You' },
]

/**
 * The Collective feed, which persona mode could not exercise at all until
 * 31 Aug 2026 — the screen rendered its empty state and every bug in it
 * (including a collapsed height that played the whole feed at once) shipped
 * unseen, because the only accounts with reachable songs live in Sydney and
 * getting into one needs an emailed code.
 *
 * These point at the real clips in app/reels-clips/, which Vite serves in dev.
 * Two shapes on purpose, because they are two different code paths: a video
 * clip, and a song whose only media is audio and which therefore has to play
 * as a lyric card.
 */
const FEED_WRITERS = {
  jordan: { first_name: 'Jordan', last_name: 'Lee' },
  morgan: { first_name: 'Morgan', last_name: 'Diaz' },
}

const FEED_SONGS = [
  {
    id: 'dev-song-holdfast', songwriter_id: USERS.jordan.id, title: 'Hold Fast',
    created_at: daysAgo(5), status: 'finished', destination: null, reach: 'collective',
    key: 'D', scripture_refs: ['Hebrews 10:23'], duration_seconds: 221,
    finished_at: daysAgo(5), submitted_at: null, submitted_to_church_id: null,
    r2_object_key: 'dev/hold-fast.mp4', song_cowriters: [],
    writer: FEED_WRITERS.jordan, users: FEED_WRITERS.jordan,
  },
  {
    id: 'dev-song-morning', songwriter_id: 'dev-morgan', title: 'Morning Will Come',
    created_at: daysAgo(9), status: 'finished', destination: null, reach: 'network',
    key: 'G', scripture_refs: ['Psalm 30:5'], duration_seconds: 242,
    finished_at: daysAgo(9), submitted_at: null, submitted_to_church_id: null,
    r2_object_key: 'dev/morning-will-come.mp4', song_cowriters: [],
    writer: FEED_WRITERS.morgan, users: FEED_WRITERS.morgan,
  },
  {
    id: 'dev-song-quiet', songwriter_id: USERS.jordan.id, title: 'Quiet Place',
    created_at: daysAgo(12), status: 'finished', destination: null, reach: 'collective',
    key: 'C', scripture_refs: ['Matthew 6:6'], duration_seconds: 198,
    finished_at: daysAgo(12), submitted_at: null, submitted_to_church_id: null,
    r2_object_key: 'dev/quiet-place.mp4', song_cowriters: [],
    writer: FEED_WRITERS.jordan, users: FEED_WRITERS.jordan,
  },
]

/** Words for the two songs that play as lyric cards. */
const FEED_SECTIONS = [
  { id: 'sec-morning-1', song_id: 'dev-song-morning', section_type: 'verse', section_number: 1,
    order_position: 1, lyrics: 'Weeping may stay for the night\nBut the morning will come' },
  { id: 'sec-morning-2', song_id: 'dev-song-morning', section_type: 'chorus', section_number: 1,
    order_position: 2, lyrics: 'I have seen the dark give way\nEvery time, without fail' },
  { id: 'sec-quiet-1', song_id: 'dev-song-quiet', section_type: 'verse', section_number: 1,
    order_position: 1, lyrics: 'In the quiet place\nWhere nothing is required of me' },
  { id: 'sec-quiet-2', song_id: 'dev-song-quiet', section_type: 'chorus', section_number: 1,
    order_position: 2, lyrics: 'You meet me anyway\nAnd that is enough' },
]

/** One video clip, so the feed has both a reel and two lyric cards in it. */
const SONG_CLIPS = [
  {
    id: 'dev-clip-holdfast', song_id: 'dev-song-holdfast', owner_id: USERS.jordan.id,
    kind: 'original', object_key: 'dev/hold-fast.mp4', duration_seconds: 58,
    width: 1080, height: 1920, created_at: daysAgo(4), removed_at: null,
    owner: FEED_WRITERS.jordan,
    song: {
      id: 'dev-song-holdfast', title: 'Hold Fast', reach: 'collective',
      scripture_refs: ['Hebrews 10:23'], songwriter_id: USERS.jordan.id,
      duration_seconds: 221, writer: FEED_WRITERS.jordan,
    },
  },
]

/**
 * A cover: Alex's take on Jordan's song. This is the cross-link the profile
 * exists to make -- the row credits Jordan and leads to Jordan's profile, so
 * two writers who have never met are connected by the song rather than by a
 * follow.
 */
const COVER_CLIPS = [
  {
    id: 'dev-clip-cover-holdfast', song_id: 'dev-song-holdfast',
    owner_id: USERS.alex.id, kind: 'cover', object_key: 'dev/quiet-place.mp4',
    duration_seconds: 41, width: 1080, height: 1920,
    created_at: daysAgo(2), removed_at: null,
    owner: { first_name: 'Alex', last_name: 'Rivers' },
    song: {
      id: 'dev-song-holdfast', title: 'Hold Fast', reach: 'collective',
      scripture_refs: ['Hebrews 10:23'], songwriter_id: USERS.jordan.id,
      duration_seconds: 221, writer: FEED_WRITERS.jordan,
      users: FEED_WRITERS.jordan,
    },
  },
]

const SONG_COSIGN_STATUS = [
  { song_id: 'dev-song-trust', all_signed: true, total_split: 100 },
]

const SONG_LICENCES = [
  {
    id: 'dev-licence-heart', song_id: 'dev-song-heart', church_id: CHURCH_ID,
    songwriter_id: USERS.alex.id, status: 'active',
    scope_rehearsal: true, scope_live: true, scope_recording: false,
    scope_streaming: true, scope_print: true,
    duration_months: 12, starts_at: daysAgo(30), expires_at: daysFromNow(335),
    on_leave_church: 'wind_down', on_leave_worship_team: 'wind_down', wind_down_days: 60,
    on_third_party_publish: 'survives', renewal: 'require_reapproval',
    accepted_at: daysAgo(30), pco_song_id: null, withdrawal_reason: null,
    wind_down_reason: null, wind_down_ends_at: null,
    songs: { title: 'Still My Heart' }, churches: { name: CHURCH.name },
    songwriter: pick(USERS.alex, ['first_name', 'last_name', 'email']),
  },
]

const SONG_LICENCE_EVENTS = [
  { id: 'evt-1', licence_id: 'dev-licence-heart', event: 'renewed',
    from_status: 'active', to_status: 'active', reason: 'auto_renew', occurred_at: daysAgo(30) },
]

const CONTENT_FLAGS = [
  { id: 'flag-1', song_id: 'dev-song-open', source: 'text_filter', severity: 'review',
    status: 'open', score: 0.62, detail: 'Lyrics reference is ambiguous — flagged for a human read, not a rule violation.',
    report_reason: null, created_at: daysAgo(1), resolved_at: null, resolution_note: null },
]

const MODERATION_DASHBOARD = [
  {
    id: 'dev-song-open', title: 'Wide Open', writer: 'Alex Rivers', church: CHURCH.name,
    status: 'awaiting_cosign', submitted_at: null,
    score: 62, open_flags: 1, publish_blocked: false, awaiting_human: true, reports: 0,
    has_media: false, media_mime: null, music_confidence: 0.94,
    last_source: 'text_filter', last_detail: 'Lyrics reference is ambiguous — flagged for a human read, not a rule violation.',
  },
  {
    id: 'dev-song-heart', title: 'Still My Heart', writer: 'Alex Rivers', church: CHURCH.name,
    status: 'active', submitted_at: daysAgo(32),
    score: 97, open_flags: 0, publish_blocked: false, awaiting_human: false, reports: 0,
    has_media: false, media_mime: null, music_confidence: 0.98,
    last_source: 'gemini_audio', last_detail: 'Clear, on-topic worship lyric. No concerns.',
  },
]

const MODERATION_OVERVIEW = [
  { songs: 4, watch: 1, trouble: 0, awaiting_human: 1, unchecked: 1, average_score: 88 },
]

const CHURCH_REVIEW_STEPS = [
  { id: 'step-1', church_id: CHURCH_ID, label: 'Writer submits', description: 'Always first · with splits & co-sign', position: 1, is_fixed: true, active: true },
  { id: 'step-2', church_id: CHURCH_ID, label: 'Worship pastor review', description: 'Ticks, listen, decision', position: 2, is_fixed: false, active: true },
  { id: 'step-3', church_id: CHURCH_ID, label: 'Pair or rework loop', description: 'Optional development pass', position: 3, is_fixed: false, active: true },
  { id: 'step-4', church_id: CHURCH_ID, label: 'Senior pastor sign-off', description: 'Theology & culture fit', position: 4, is_fixed: false, active: true },
  { id: 'step-5', church_id: CHURCH_ID, label: 'Two-Sunday congregation test', description: 'Does the room sing it', position: 5, is_fixed: false, active: true },
  { id: 'step-6', church_id: CHURCH_ID, label: 'Into rotation', description: 'Always last', position: 6, is_fixed: true, active: true },
]

function pick(obj, keys) {
  return Object.fromEntries(keys.map((k) => [k, obj[k]]))
}
function daysAgo(n) { return new Date(Date.now() - n * 86400000).toISOString() }
function daysFromNow(n) { return new Date(Date.now() + n * 86400000).toISOString() }

/** A fresh, independently-mutable copy of the shared world — called once per
 *  persona so clicking "approve" as Pat doesn't also change what Sam sees on
 *  the next reload. */
// Three support messages covering the three states the screen has to draw:
// unanswered, answered, and answered-then-closed. Alex is the sender on all of
// them so the writer's own /support screen has something to show too — that
// side is the half nobody could see before replies existed.
//
// `sender` and `replier` are pre-attached rather than resolved, per the note at
// the top of mockSupabaseClient: the mock does not do embedded joins.
const FEEDBACK = [
  {
    id: 'fb-open', user_id: USERS.alex.id, kind: 'issue', scope: 'global', church_id: null,
    subject: 'Chart prints without the last verse',
    body: 'I opened Wide Open, tapped Chart, then Print.\nThe last verse is missing every time.',
    app_version: 'dev · fixture', user_agent: 'Mozilla/5.0 (fixture)', path: '/support/issue',
    status: 'open', created_at: new Date(Date.now() - 2 * 3.6e6).toISOString(),
    handled_at: null, handled_by: null,
    reply_body: null, replied_at: null, replied_by: null,
    sender: USERS.alex, replier: null, church: null,
  },
  {
    id: 'fb-replied', user_id: USERS.alex.id, kind: 'feature', scope: 'global', church_id: null,
    subject: 'Let two writers edit the same sheet',
    body: 'We write in the same room on a Tuesday and end up emailing versions around.',
    app_version: 'dev · fixture', user_agent: 'Mozilla/5.0 (fixture)', path: '/support/feature',
    status: 'read', created_at: new Date(Date.now() - 3 * 8.64e7).toISOString(),
    handled_at: null, handled_by: null,
    reply_body: 'This is on the list — co-writing in the same room is exactly the case we built '
      + 'the co-sign flow around, and live editing is the obvious next step.\n\nNo date yet, but '
      + 'you will hear from us when it lands.',
    replied_at: new Date(Date.now() - 2 * 8.64e7).toISOString(), replied_by: USERS.spm.id,
    sender: USERS.alex, replier: USERS.spm, church: null,
  },
  {
    id: 'fb-closed', user_id: USERS.alex.id, kind: 'issue', scope: 'global', church_id: null,
    subject: 'Could not upload a voice memo',
    body: 'It sat at 0% for a minute and then said something went wrong.',
    app_version: 'dev · fixture', user_agent: 'Mozilla/5.0 (fixture)', path: '/song/new',
    status: 'closed', created_at: new Date(Date.now() - 9 * 8.64e7).toISOString(),
    handled_at: new Date(Date.now() - 7 * 8.64e7).toISOString(), handled_by: USERS.spm.id,
    reply_body: 'Found it — the upload was timing out on files over about 40MB. Fixed and shipped. '
      + 'Thank you for the report, it was the only one we had.',
    replied_at: new Date(Date.now() - 7 * 8.64e7).toISOString(), replied_by: USERS.spm.id,
    sender: USERS.alex, replier: USERS.spm, church: null,
  },
]

/**
 * Profiles and follows, so the writer profile and the follow button can be
 * exercised without a real account. songwriter_profiles is a VIEW in Postgres;
 * here it is just rows, which is all the mock client needs.
 *
 * The follow rows carry their joined user object under the same alias each
 * screen selects, because the mock's select() returns rows verbatim rather
 * than resolving embeds.
 */
const SONGWRITER_PROFILES = [
  { id: USERS.jordan.id, first_name: 'Jordan', last_name: 'Lee', avatar_url: null,
    bio: 'Leads worship at a plant in the outer east. Writes most things on an '
       + 'acoustic at the kitchen table after the kids are down.',
    tagline: 'Songs for small rooms', location: 'Melbourne', state: 'VIC',
    created_at: daysAgo(300), skills: [] },
  { id: 'dev-morgan', first_name: 'Morgan', last_name: 'Diaz', avatar_url: null,
    bio: 'Writing hymns that sound like now.', tagline: null,
    location: 'Geelong', state: 'VIC', created_at: daysAgo(210), skills: [] },
  { id: USERS.alex.id, first_name: 'Alex', last_name: 'Rivers', avatar_url: null,
    bio: null, tagline: null, location: null, state: null,
    created_at: daysAgo(120), skills: [] },
]

const FOLLOWS = [
  { follower_id: USERS.sam.id, writer_id: USERS.jordan.id, created_at: daysAgo(11),
    followers: { first_name: 'Sam', last_name: 'Okafor' },
    users: { id: USERS.sam.id, first_name: 'Sam', last_name: 'Okafor' } },
  { follower_id: USERS.pat.id, writer_id: USERS.jordan.id, created_at: daysAgo(4),
    followers: { first_name: 'Pat', last_name: 'Whitfield' },
    users: { id: USERS.pat.id, first_name: 'Pat', last_name: 'Whitfield' } },
]

function buildWorld() {
  return {
    songwriter_profiles: structuredClone(SONGWRITER_PROFILES),
    follows: structuredClone(FOLLOWS),
    feedback: structuredClone(FEEDBACK),
    churches: [structuredClone(CHURCH)],
    users: Object.values(USERS).map((u) => structuredClone(u)),
    church_members: structuredClone(CHURCH_MEMBERS),
    songs: structuredClone([...SONGS, ...FEED_SONGS]),
    song_cowriters: structuredClone(SONG_COWRITERS),
    song_sections: structuredClone([...SONG_SECTIONS, ...FEED_SECTIONS]),
    song_clips: structuredClone([...SONG_CLIPS, ...COVER_CLIPS]),
    song_cosign_status: structuredClone(SONG_COSIGN_STATUS),
    song_licences: structuredClone(SONG_LICENCES),
    song_licence_events: structuredClone(SONG_LICENCE_EVENTS),
    content_flags: structuredClone(CONTENT_FLAGS),
    moderation_dashboard: structuredClone(MODERATION_DASHBOARD),
    moderation_overview: structuredClone(MODERATION_OVERVIEW),
    church_review_steps: structuredClone(CHURCH_REVIEW_STEPS),
    subscriptions: [],
    user_skills: [],
  }
}

function sessionFor(user) {
  return {
    user: {
      id: user.id, email: user.email,
      user_metadata: { first_name: user.first_name, lane_set: true, access_type: 'church' },
    },
  }
}

export const DEV_PERSONAS = [
  {
    id: 'alex', label: 'Alex — Songwriter', landing: '/me',
    blurb: 'A finished song and a licence at Riverside, one draft, one pending co-write.',
    user: USERS.alex,
  },
  {
    id: 'jordan', label: 'Jordan — Co-writer (50%)', landing: '/me',
    blurb: "Named on Alex's song \"Wide Open\" at 50%, not yet signed.",
    user: USERS.jordan,
  },
  {
    id: 'pat', label: 'Pat — Church Admin', landing: '/admin/people',
    blurb: 'Riverside Community Church — people, process, and one licence to review.',
    user: USERS.pat,
  },
  {
    id: 'sam', label: 'Sam — Reviewer', landing: '/inbox',
    blurb: '"Learning To Trust" is waiting in the queue.',
    user: USERS.sam,
  },
  {
    id: 'spm', label: 'SPM — Platform Staff', landing: '/admin/moderation',
    blurb: 'Two songs on the moderation board, one waiting on a human.',
    user: USERS.spm,
  },
]

export function buildPersona(id) {
  const def = DEV_PERSONAS.find((p) => p.id === id)
  if (!def) return null
  return { ...def, db: buildWorld(), session: sessionFor(def.user) }
}
